import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// EN-default, NO navigator auto-detect (ADR-0011 / ADR-0016).
const en = {
  'app.title': 'GeoLab',
  'app.tagline': 'Browser-native geospatial tools, your data never leaves your machine.',
  'nav.workbench': 'Workbench',
  'nav.pipeline': 'Pipeline',
  'nav.tools': 'Tools',
  'nav.credits': 'Credits',
  'tools.title': 'Tool catalog',
  'tools.intro':
    'Load the geolibre engine (~22 MB WASM, runs entirely in your browser) to list its 747 real WhiteboxTools + GeoLibre tools, grouped by category, each with its provenance and license. More engines (Turf, GDAL, GEOS, H3, …) plug in next via their own adapters.',
  'tools.load': 'Load engine, 747 tools',
  'tools.loading': 'Loading WASM engine…',
  'tools.loaded': 'tools loaded in your browser',
  'tools.search': 'Search tools…',
  'tools.allCats': 'All',
  'tools.details': 'Details',
  'tools.noMatch': 'No tools match your search.',
  'tools.prev': 'Prev',
  'tools.next': 'Next',
  'tools.page': 'Page',
  'wb.introTitle': 'A real analysis, in your browser',
  'wb.intro':
    'Generate a synthetic DEM (or upload your own GeoTIFF / GeoJSON), pick any of geolibre’s 747 real WhiteboxTools / GeoLibre tools, and run it entirely client-side via the WASM engine. Each result becomes a layer you can inspect (colormap + value read-out at the cursor). No server, your data never leaves the page.',
  'wb.step1': 'Generate a sample DEM',
  'wb.gen': 'Generate sample DEM',
  'wb.addSample': 'Add sample…',
  'wb.demo': 'Demo workspace',
  'wb.engineLoading': 'Loading the WASM engine…',
  'wb.run': 'Run Slope',
  'wb.running': 'Running…',
  'wb.dem': 'DEM, elevation',
  'wb.slope': 'Slope, degrees',
  'wb.log': 'Run log',
  'wb.note': 'Synthetic sample data, labeled as such. See all engines on',
  'wb.upload': 'Upload layer',
  'wb.colormap': 'Colormap',
  'wb.runTool': 'Run tool',
  'wb.pickTool': 'Pick a tool from the toolbox to configure and run it on the active layer.',
  'wb.toolboxHint': 'Generate a sample DEM or upload a GeoTIFF / GeoJSON to load the toolbox (747 tools).',
  'wb.canvasHint': 'Generate or upload a raster to see it here. Run a tool to add its result as a new layer.',
  'wb.uploadHint': 'GeoTIFF (.tif) or GeoJSON (.geojson)',
  'wb.cancel': 'Cancel',
  'wb.workerNote': 'Runs in a Web Worker, UI stays responsive. ·',
  'wb.vectorMapOnly': 'Vector layers are shown in Map view, switch to Map.',
  'toolbox.title': 'Toolbox',
  'toolbox.search': 'Search 747 tools…',
  'layers.title': 'Layers',
  'layers.none': 'No layers yet, generate a DEM or upload a GeoTIFF / GeoJSON.',
  'scaffold.title': 'Early build (v0.02.000), the geolibre engine runs live on the Tools page',
  'scaffold.body':
    'Architecture is in place (ADR-0059): a multi-engine tool registry with per-tool provenance. Engine adapters, the live map and the pipeline editor are landing next. Below is the engine catalog GeoLab integrates.',
  'workbench.engines': 'Integrated engines',
  'workbench.toolsApprox': '≈ tools',
  'workbench.status': 'status',
  'workbench.mapPlaceholder': 'The interactive map / 3D canvas lands here (MapLibre + deck.gl + Potree).',
  'credits.title': 'Credits & licenses',
  'credits.intro':
    'GeoLab stands on open-source work. Every tool shows its source; here is the full list. We claim authorship only of GeoLab and its own tools.',
  'col.engine': 'Engine',
  'col.project': 'Upstream project',
  'col.authors': 'Authors',
  'col.license': 'License',
  'col.tools': '≈ Tools',
  'col.status': 'Status',
  'footer.lab': 'a CAOS research investigation',
  'footer.by': 'Developed by Felipe Santibáñez-Leal',
  'footer.license': 'MIT',
  'theme.toggle': 'Toggle theme',
  'lang.toggle': 'Español',
  'arch.open': 'How it works',
  'pl.title': 'Pipeline Editor',
  'pl.tagline': 'Chain tools into a DAG, run, save, and share your analysis as a Recipe.',
  'pl.loadingTools': 'Loading tool catalog…',
  'pl.run': 'Run pipeline',
  'pl.running': 'Running…',
  'pl.save': 'Save Recipe',
  'pl.load': 'Load Recipe',
  'pl.removeNode': 'Remove node',
  'pl.nodes': 'nodes',
  'pl.edges': 'edges',
  'pl.emptyHint': 'Add tools from the sidebar, drag nodes, connect handles to wire outputs to inputs.',
  'pl.pickNode': 'Click a node to configure its parameters.',
  'pl.pipelineLog': 'Pipeline run log',
};

const es: typeof en = {
  'app.title': 'GeoLab',
  'app.tagline': 'Herramientas geoespaciales en el navegador, tus datos nunca salen de tu equipo.',
  'nav.workbench': 'Banco de trabajo',
  'nav.pipeline': 'Pipeline',
  'nav.tools': 'Herramientas',
  'nav.credits': 'Créditos',
  'tools.title': 'Catálogo de herramientas',
  'tools.intro':
    'Carga el motor geolibre (~22 MB WASM, corre completo en tu navegador) para listar sus 747 herramientas reales de WhiteboxTools + GeoLibre, agrupadas por categoría, cada una con su procedencia y licencia. Más motores (Turf, GDAL, GEOS, H3, …) se enchufan luego con sus propios adaptadores.',
  'tools.load': 'Cargar motor, 747 herramientas',
  'tools.loading': 'Cargando motor WASM…',
  'tools.loaded': 'herramientas cargadas en tu navegador',
  'tools.search': 'Buscar herramientas…',
  'tools.allCats': 'Todas',
  'tools.details': 'Detalles',
  'tools.noMatch': 'Ninguna herramienta coincide con tu búsqueda.',
  'tools.prev': 'Anterior',
  'tools.next': 'Siguiente',
  'tools.page': 'Página',
  'wb.introTitle': 'Un análisis real, en tu navegador',
  'wb.intro':
    'Genera un DEM sintético (o sube tu propio GeoTIFF / GeoJSON), elige cualquiera de las 747 herramientas reales de WhiteboxTools / GeoLibre, y córrela completamente del lado del cliente con el motor WASM. Cada resultado se vuelve una capa que puedes inspeccionar (paleta + lectura de valor en el cursor). Sin servidor, tus datos nunca salen de la página.',
  'wb.step1': 'Generar un DEM de muestra',
  'wb.gen': 'Generar DEM de muestra',
  'wb.addSample': 'Agregar muestra…',
  'wb.demo': 'Workspace demo',
  'wb.engineLoading': 'Cargando el motor WASM…',
  'wb.run': 'Correr Slope',
  'wb.running': 'Corriendo…',
  'wb.dem': 'DEM, elevación',
  'wb.slope': 'Pendiente, grados',
  'wb.log': 'Registro de ejecución',
  'wb.note': 'Datos de muestra sintéticos, rotulados como tales. Ve todos los motores en',
  'wb.upload': 'Subir capa',
  'wb.colormap': 'Paleta',
  'wb.runTool': 'Correr herramienta',
  'wb.pickTool': 'Elige una herramienta del toolbox para configurarla y correrla sobre la capa activa.',
  'wb.toolboxHint': 'Genera un DEM de muestra o sube un GeoTIFF / GeoJSON para cargar el toolbox (747 herramientas).',
  'wb.uploadHint': 'GeoTIFF (.tif) o GeoJSON (.geojson)',
  'wb.canvasHint': 'Genera o sube un raster para verlo aquí. Corre una herramienta para agregar su resultado como nueva capa.',
  'wb.cancel': 'Cancelar',
  'wb.workerNote': 'Corre en un Web Worker, la UI queda responsiva. ·',
  'wb.vectorMapOnly': 'Las capas vectoriales se muestran en vista Mapa, cambia a Mapa.',
  'toolbox.title': 'Toolbox',
  'toolbox.search': 'Buscar 747 herramientas…',
  'layers.title': 'Capas',
  'layers.none': 'Sin capas aún, genera un DEM o sube un GeoTIFF / GeoJSON.',
  'scaffold.title': 'Versión temprana (v0.02.000), el motor geolibre corre en vivo en la página Herramientas',
  'scaffold.body':
    'La arquitectura ya está (ADR-0059): un registro multi-motor con procedencia por herramienta. Los adaptadores de motores, el mapa en vivo y el editor de pipelines vienen a continuación. Abajo, el catálogo de motores que GeoLab integra.',
  'workbench.engines': 'Motores integrados',
  'workbench.toolsApprox': '≈ herramientas',
  'workbench.status': 'estado',
  'workbench.mapPlaceholder': 'Aquí va el mapa / canvas 3D interactivo (MapLibre + deck.gl + Potree).',
  'credits.title': 'Créditos y licencias',
  'credits.intro':
    'GeoLab se apoya en trabajo open-source. Cada herramienta muestra su origen; aquí está la lista completa. Solo reclamamos autoría de GeoLab y sus herramientas propias.',
  'col.engine': 'Motor',
  'col.project': 'Proyecto original',
  'col.authors': 'Autores',
  'col.license': 'Licencia',
  'col.tools': '≈ Herramientas',
  'col.status': 'Estado',
  'footer.lab': 'una investigación CAOS',
  'footer.by': 'Desarrollado por Felipe Santibáñez-Leal',
  'footer.license': 'MIT',
  'theme.toggle': 'Cambiar tema',
  'lang.toggle': 'English',
  'arch.open': 'Cómo funciona',
  'pl.title': 'Editor de Pipeline',
  'pl.tagline': 'Encadena herramientas en un DAG, ejecuta, guarda y comparte tu análisis como una Receta.',
  'pl.loadingTools': 'Cargando catálogo de herramientas…',
  'pl.run': 'Correr pipeline',
  'pl.running': 'Corriendo…',
  'pl.save': 'Guardar Receta',
  'pl.load': 'Cargar Receta',
  'pl.removeNode': 'Eliminar nodo',
  'pl.nodes': 'nodos',
  'pl.edges': 'conexiones',
  'pl.emptyHint': 'Agrega herramientas desde la barra lateral, arrastra nodos, conecta los handles para encadenar salidas con entradas.',
  'pl.pickNode': 'Haz clic en un nodo para configurar sus parámetros.',
  'pl.pipelineLog': 'Registro del pipeline',
};

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, es: { translation: es } },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

export default i18n;
